import React, { useState } from 'react';
import './MacdExitConditionsBar.css';

/**
 * Exit condition status for MACD Reversion trade groups
 */
export interface MacdExitConditions {
  currentPrice: number;
  stopLoss?: number;
  profitTarget?: number;
  entryPrice?: number;
}

/**
 * Props for MacdExitConditionsBar component
 */
export interface MacdExitConditionsBarProps {
  exitConditions?: MacdExitConditions;
  isLoading?: boolean;
  className?: string;
}

/**
 * MacdExitConditionsBar Component
 *
 * Displays exit conditions for MACD Reversion trade groups:
 * - Stop-Loss price proximity
 * - Profit Target price proximity
 */
const MacdExitConditionsBar: React.FC<MacdExitConditionsBarProps> = ({
  exitConditions,
  isLoading = false,
  className = '',
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className={`macd-exit-conditions-bar ${className}`}>
        <div className="exit-targets-display loading">
          <div className="exit-target-card skeleton">
            <div className="skeleton-shimmer"></div>
          </div>
          <div className="exit-target-card skeleton">
            <div className="skeleton-shimmer"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!exitConditions) {
    return null;
  }

  const { currentPrice, stopLoss, profitTarget } = exitConditions;

  const calculateProximity = (targetPrice: number | undefined): number => {
    if (!targetPrice || !currentPrice || currentPrice <= 0) return 0;
    return ((targetPrice - currentPrice) / currentPrice) * 100;
  };

  const stopLossProximity = calculateProximity(stopLoss);
  const profitTargetProximity = calculateProximity(profitTarget);

  const getStatus = (proximity: number): 'at-target' | 'near' | 'far' => {
    const absProximity = Math.abs(proximity);
    if (absProximity < 0.5) return 'at-target';
    if (absProximity < 2.0) return 'near';
    return 'far';
  };

  const formatPrice = (price: number): string => {
    if (price < 1) {
      return `$${price.toFixed(4)}`;
    }
    return `$${price.toFixed(2)}`;
  };

  type ExitCondition = {
    label: string;
    displayValue: string;
    proximity: string;
    status: 'at-target' | 'near' | 'far';
    critical: boolean;
  };

  const conditions: ExitCondition[] = [];

  if (stopLoss) {
    conditions.push({
      label: 'Stop Loss',
      displayValue: formatPrice(stopLoss),
      proximity: `${stopLossProximity > 0 ? '+' : ''}${stopLossProximity.toFixed(2)}%`,
      status: getStatus(stopLossProximity),
      critical: stopLossProximity > -2.0,
    });
  }

  if (profitTarget) {
    conditions.push({
      label: 'Profit Target',
      displayValue: formatPrice(profitTarget),
      proximity: `${profitTargetProximity > 0 ? '+' : ''}${profitTargetProximity.toFixed(2)}%`,
      status: getStatus(profitTargetProximity),
      critical: false,
    });
  }

  const getSummaryText = (): string => {
    if (profitTarget && profitTargetProximity > 0) {
      return `Target at ${formatPrice(profitTarget)} (+${profitTargetProximity.toFixed(2)}%)`;
    }
    return 'Monitoring exit conditions';
  };

  return (
    <div className={`macd-exit-conditions-bar ${className}`}>
      <div className="exit-targets-summary">
        {getSummaryText()}
      </div>

      <div className="exit-targets-display">
        {conditions.map((condition, index) => {
          const { label, displayValue, proximity, status, critical } = condition;

          let cardClass = `exit-target-card ${status}`;
          if (critical) cardClass += ' critical';

          let color = '#A78BFA';
          if (critical) color = '#ef4444';
          else if (status === 'at-target') color = '#10b981';
          else if (status === 'near') color = '#f59e0b';

          return (
            <div
              key={index}
              className={cardClass}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              role="status"
              tabIndex={0}
            >
              <div className="target-label">{label}</div>
              <div className="target-price" style={{ color }}>{displayValue}</div>
              <div className="target-distance">{proximity}</div>

              {hoveredIndex === index && (
                <div className="exit-condition-tooltip" role="tooltip">
                  <div className="tooltip-header">{label}</div>
                  <div className="tooltip-detail">Target Price: {displayValue}</div>
                  <div className="tooltip-proximity">{proximity}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MacdExitConditionsBar;
